@if (isAbortingTerminal) {
  <div
    class="fixed inset-0 z-50 flex flex-col items-center justify-center bg-white px-10 text-center"
  >
    @if (!abortTerminalFailed) {
      <i class="icon icon-privacy animate-pulse text-5xl"></i>
      <p class="mt-6 text-2xl font-bold">Annulation en cours…</p>
    }
    @if (abortTerminalFailed) {
      <p class="text-2xl font-bold">L'annulation n'a pas pu être finalisée</p>
      <p class="mt-4 text-base text-neutral-600">
        Veuillez vérifier que la transaction a bien été annulée sur le terminal de paiement avant de
        continuer.
      </p>
      <div class="mt-8">
        <app-button-atom
          size="L"
          type="primary"
          label="Forcer le retour"
          (clickEventEmitter)="forceContinue()"
        ></app-button-atom>
      </div>
    }
  </div>
}

@if (availablePaymentMethods) {
  <div
    class="w-full md:mx-auto md:max-w-md"
    [style.color]="textColor() | contentBuilderColor"
  >
    <div
      class="mt-6 lg:mt-12 kiosk:mb-16 kiosk:mt-12"
      [ngClass]="remainingTime() ? 'lg:mb-6' : 'lg:mb-12'"
    >
      <h1
        class="mb-3 mt-6 hidden text-center font-accent text-5xl font-bold md:block"
        [style.color]="headingColor() | contentBuilderColor"
      >
        {{ 'payment.title' | translate }}
      </h1>
      @if (availablePaymentMethods.length > 1) {
        <h2
          class="mb-3 mt-5 px-5 font-accent text-md font-bold md:mt-6 md:text-center"
          [style.color]="headingColor() | contentBuilderColor"
        >
          {{ 'payment.subtitle' | translate }}
        </h2>
      }
      @if (displayRemainingMinutes()) {
        <p class="mx-auto mb-2 px-5 font-medium text-error-700 md:text-center">
          {{ 'payment.finish-before-x-minutes' | translate: { minutes: remainingMinutes() } }}
        </p>
      }
    </div>
    <div
      class="border-t border-neutral-200 md:rounded-md md:border overflow-hidden"
      [ngClass]="{
        'mb-4': bottomMargin() === 'small',
        'mb-80': bottomMargin() === 'large',
      }"
    >
      @for (paymentMethod of availablePaymentMethods; track paymentMethod.handle) {
        <div
          class="border-b border-neutral-200 bg-white"
          [class.disabled]="
            isPaymentMethodSelectionLocked && paymentMethod.handle !== selectedPaymentMethod?.handle
          "
        >
          <div class="flex flex-col px-5">
            <div
              class="flex items-center justify-between py-4.5"
              [class.cursor-pointer]="
                availablePaymentMethods.length > 1 && !isPaymentMethodSelectionLocked
              "
              [class.cursor-not-allowed]="
                isPaymentMethodSelectionLocked &&
                paymentMethod.handle !== selectedPaymentMethod?.handle
              "
              (click)="
                availablePaymentMethods.length > 1
                  ? selectPaymentMethod(paymentMethod)
                  : $event.stopPropagation()
              "
            >
              <div class="flex w-4/6 items-center">
                @if (availablePaymentMethods.length > 1) {
                  <span class="pr-1.5 text-xs">
                    <input
                      type="radio"
                      [ngClass]="{
                        'p-2.5 bg-primary-600 border-primary-600 m-1 shadow-custom':
                          paymentMethod.handle === selectedPaymentMethod?.handle,
                      }"
                      class="form-radio m-2 border-2 border-neutral-400 bg-none p-1.5"
                      [checked]="paymentMethod.handle === selectedPaymentMethod?.handle"
                    />
                  </span>
                }
                @if (
                  (paymentMethod.label === 'payment.on-site' ||
                    paymentMethod.label === 'payment.house-account') &&
                  (order$ | async)?.[orderKeys.FinalPrice] === 0
                ) {
                  <span>{{ 'payment.on-site-zero' | translate }}</span>
                } @else if (
                  (paymentMethod.label === 'payment.on-site' ||
                    paymentMethod.label === 'payment.house-account') &&
                  $any(paymentMethod.data)?.customer_group_label
                ) {
                  <span>{{ $any(paymentMethod.data)?.customer_group_label }}</span>
                } @else {
                  <span>{{ paymentMethod.label | translate }}</span>
                }
              </div>
              <div class="flex w-2/6 justify-end">
                @for (logo of paymentMethod.logos; track $index) {
                  <img
                    alt="logo"
                    class="ml-2 max-h-6"
                    [src]="logo"
                  />
                }
              </div>
            </div>
            @if (paymentMethod.handle === selectedPaymentMethod?.handle) {
              @if (isPreview) {
                <div class="px-5 pb-4 text-sm text-neutral-500">
                  {{ 'payment.preview-panel-placeholder' | translate }}
                </div>
              } @else {
                <app-component-factory
                  [componentClass]="paymentMethod.panelComponent"
                  [data]="getPanelData(paymentMethod)"
                ></app-component-factory>
              }
            }
          </div>
        </div>
      }
      <div class="hidden md:block">
        @if (orderFinalPrice !== null || (order$ | async)) {
          <div class="pt-8 text-center">
            <span class="mr-1.5 font-accent text-2xl font-bold">Total</span>
            <div class="md:hidden">
              <app-price-atom
                [amount]="
                  amount ?? orderFinalPrice ?? (order$ | async)?.[orderKeys.FinalPrice] ?? 0
                "
                [format]="true"
                [size]="'MD'"
              ></app-price-atom>
            </div>
            <div class="hidden md:inline-block">
              <app-price-atom
                [amount]="
                  amount ?? orderFinalPrice ?? (order$ | async)?.[orderKeys.FinalPrice] ?? 0
                "
                [format]="true"
                [size]="'2XL'"
              ></app-price-atom>
            </div>
          </div>
        }
        @if (error) {
          <div class="flex justify-center px-4 pb-4 text-center">
            <span class="font-base text-error-600">
              {{ error | translate }}
            </span>
          </div>
        }
        @if (hasErrorMessages) {
          <div>
            @for (message of errorMessages; track $index) {
              <p class="my-5 rounded bg-error-50 px-4 py-3 font-base text-sm text-error-700">
                {{ message }}
              </p>
            }
          </div>
        }
        <div class="mb-10 flex justify-center pt-4">
          @if (button.isVisible) {
            <app-button-atom
              [icon]="
                !(status.isInProgress || status.isLoading || !hasCheckedAssociatedPaidOrder())
                  ? 'icon-black-lock'
                  : ''
              "
              [iconUrl]="
                status.isInProgress || status.isLoading || !hasCheckedAssociatedPaidOrder()
                  ? 'assets/images/spinner.svg'
                  : ''
              "
              [label]="
                (button.text === 'payment.validate-on-site' &&
                (order$ | async)?.[orderKeys.FinalPrice] === 0
                  ? 'payment.validate-on-site-zero'
                  : button.text
                ) | translate
              "
              [type]="'primary'"
              [size]="'M'"
              [disabled]="!button.isEnabled || status.isInProgress || hasErrorMessages"
              (click)="triggerPayment()"
            ></app-button-atom>
          }
        </div>
      </div>
    </div>
  </div>
}

<div class="fixed bottom-0 left-0 right-0 z-30 bg-white shadow shadow-top md:hidden">
  <div
    class="container mx-auto p-4 pt-4.5 align-middle"
    [class.pb-7]="isNotched"
  >
    @if (error) {
      <div class="flex justify-between pb-4">
        <span class="font-base text-error-600">
          {{ error | translate }}
        </span>
      </div>
    }
    @if (orderFinalPrice !== null || (order$ | async)) {
      <div class="flex items-center justify-center">
        <span class="mr-1.5 font-accent text-md font-bold">Total</span>
        <app-price-atom
          [amount]="amount ?? orderFinalPrice ?? (order$ | async)?.[orderKeys.FinalPrice] ?? 0"
          [format]="true"
          [size]="'MD'"
        ></app-price-atom>
      </div>
    }
    @if (hasErrorMessages) {
      <div>
        @for (message of errorMessages; track $index) {
          <p class="px-4 pt-4 text-base font-normal text-error-700">
            {{ message }}
          </p>
        }
      </div>
    }

    @if (button.isVisible) {
      <div class="pt-4.5">
        <app-button-atom
          [iconUrl]="status.isInProgress || status.isLoading ? 'assets/images/spinner.svg' : ''"
          [label]="
            (button.text === 'payment.validate-on-site' &&
            (order$ | async)?.[orderKeys.FinalPrice] === 0
              ? 'payment.validate-on-site-zero'
              : button.text
            ) | translate
          "
          [type]="'primary'"
          [size]="'L'"
          [disabled]="
            !button.isEnabled ||
            status.isInProgress ||
            hasErrorMessages ||
            !hasCheckedAssociatedPaidOrder()
          "
          (click)="triggerPayment()"
        ></app-button-atom>
      </div>
    }
  </div>
</div>

@if (status.isInProgress) {
  <app-payment-loading-screen-atom></app-payment-loading-screen-atom>
}
